import { useEffect, useRef, useSyncExternalStore } from "react";
import { subscribe, getSnapshot, ensureLoaded } from "../models/quotesStore";

/**
 * One quote for a loading screen, held steady for the life of the component —
 * or, with a `rotationKey`, steady for as long as that key stays the same.
 *
 * The corpus lives in the cloud, so it may not be there on the very first paint
 * of a first-ever visit. Rather than block rendering, the spinner appears
 * immediately and the quote lands as soon as the corpus does — in practice well
 * inside the deliberate 1.5s minimum the loading treatment already holds.
 * Returns "" while nothing is known, so callers render the spinner alone.
 *
 * `rotationKey` lets a long-lived component ask for a fresh quote when some
 * value changes (e.g. PhotoGallery re-picking per page) without re-picking on
 * every render. Omit it to pick once per mount, as SiteLoading and HomeView do.
 */
export default function useQuote(rotationKey) {
  const quotes = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  // Remembers which key the current pick belongs to, so a corpus update alone
  // (same key) never re-picks — that would make the quote flicker the moment
  // the fetch resolves. Only a changed key, or the first pick once a corpus
  // exists, triggers a new one.
  const picked = useRef({ key: rotationKey, index: null });

  useEffect(() => {
    ensureLoaded();
  }, []);

  if (quotes.length === 0) return "";

  const needsPick =
    picked.current.index === null ||
    picked.current.index >= quotes.length ||
    picked.current.key !== rotationKey;
  if (needsPick) {
    picked.current = { key: rotationKey, index: Math.floor(Math.random() * quotes.length) };
  }
  return quotes[picked.current.index];
}
